import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { StaffInvoice, StaffInvoiceInput } from './types';

export const staffInvoiceKeys = {
  all: ['staff-invoices'] as const,
  lists: () => [...staffInvoiceKeys.all, 'list'] as const,
  list: (params: api.StaffInvoiceListParams) => [...staffInvoiceKeys.lists(), params] as const,
  detail: (id: string | number) => [...staffInvoiceKeys.all, 'detail', id] as const,
};

export function useStaffInvoices(
  params: api.StaffInvoiceListParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: staffInvoiceKeys.list(params),
    queryFn: () => api.listStaffInvoices(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

/** One employee's `{id, name, user}` for the staff-invoice form (DEV13-77). */
export function useStaffInvoiceEmployee(employeeId: number) {
  return useQuery({
    queryKey: [...staffInvoiceKeys.all, 'employee', employeeId] as const,
    queryFn: () => api.getStaffInvoiceEmployee(employeeId),
    enabled: employeeId > 0,
  });
}

/** The salary in effect at the invoice's period end + the hourly rate it
 *  implies, for the rate prefill (DEV13-77, BE ADR-104). `periodEnd` '' = today. */
export function useStaffInvoiceSalary(employeeId: number, periodEnd = '') {
  return useQuery({
    queryKey: [...staffInvoiceKeys.all, 'salary', employeeId, periodEnd] as const,
    queryFn: () => api.getStaffInvoiceSalary(employeeId, periodEnd),
    enabled: employeeId > 0,
  });
}

export function useNextStaffInvoiceNumber(employeeId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...staffInvoiceKeys.all, 'next-number', employeeId] as const,
    queryFn: () => api.getNextStaffInvoiceNumber(employeeId),
    enabled: enabled && employeeId > 0,
    staleTime: 0,
  });
}

export function useStaffInvoice(id: string | number) {
  return useQuery({
    queryKey: staffInvoiceKeys.detail(id),
    queryFn: () => api.getStaffInvoice(id),
    enabled: id !== '' && id != null,
  });
}

export function useCreateStaffInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StaffInvoiceInput) => api.createStaffInvoice(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffInvoiceKeys.lists() });
      toast.success('Staff invoice created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateStaffInvoice(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<StaffInvoiceInput>) => api.updateStaffInvoice(id, input),
    onSuccess: (inv) => {
      qc.invalidateQueries({ queryKey: staffInvoiceKeys.lists() });
      qc.setQueryData(staffInvoiceKeys.detail(id), inv);
      toast.success('Staff invoice updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteStaffInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteStaffInvoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffInvoiceKeys.lists() });
      toast.success('Staff invoice deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/** Lifecycle actions (issue / generate PDF / mark paid) keyed by public_id. */
export function useStaffInvoiceAction(
  fn: (publicId: string) => Promise<StaffInvoice>,
  successMessage: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => fn(publicId),
    onSuccess: (inv) => {
      qc.invalidateQueries({ queryKey: staffInvoiceKeys.lists() });
      qc.setQueryData(staffInvoiceKeys.detail(inv.public_id), inv);
      toast.success(successMessage);
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
